import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Alert, Box, Button, Card, CardContent, Checkbox, Chip, CircularProgress, Dialog,
  DialogActions, DialogContent, DialogTitle, Divider, FormControlLabel, InputAdornment,
  MenuItem, Stack, Tab, Tabs, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, TextField, Typography,
} from '@mui/material';
import { EditLocationAlt as AssignIcon, Search as SearchIcon } from '@mui/icons-material';
import * as orgApi from '../api/organization';

const ROLE_LABELS = {
  clinic_admin: 'Site admin',
  provider: 'Provider',
  staff: 'Staff',
};
// How each role reads in the Roles column, most senior first. One person can hold
// several — a provider who is also an administrator shows both.
const ROLE_CHIPS = [
  { key: 'org_admin', label: 'Org admin', color: 'secondary' },
  { key: 'clinic_admin', label: 'Admin', color: 'warning' },
  { key: 'provider', label: 'Provider', color: 'primary' },
  { key: 'staff', label: 'Staff', color: 'default' },
];

// Tabs, as on the enterprise and region People page, with a count on each.
const TABS = [
  { key: 'all', label: 'Everyone' },
  { key: 'provider', label: 'Providers' },
  { key: 'staff', label: 'Staff' },
  { key: 'clinic_admin', label: 'Site admins' },
  { key: 'org_admin', label: 'Org admins' },
];

/**
 * Everyone in the organisation, listed ONCE with every site they work at.
 *
 * The point of this screen over the per-site rosters: seeing that Dr Chen covers three
 * of your sites, and being able to change that in one action instead of opening three
 * clinics in turn. The dialog sends the whole checkbox state and the server reconciles
 * it — grants what's new, revokes what was unticked.
 */
export default function OrgTeam() {
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);
  const [orgAdminIds, setOrgAdminIds] = useState(new Set());
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [role, setRole] = useState('provider');
  const [checked, setChecked] = useState([]);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState(0);
  const [search, setSearch] = useState('');
  const [site, setSite] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [data, adminData] = await Promise.all([
        orgApi.listMembers(),
        orgApi.listOrgAdmins().catch(() => ({ admins: [] })),
      ]);
      // The members list is everyone with a role AT a site. An organisation admin who
      // works at none of them is still one of the people running it, so they are listed
      // too — with nothing under Works at — or the Org admins tab would always read 0.
      const listed = new Set((data.members || []).map((m) => m.id));
      const adminsOnly = (adminData.admins || []).filter((a) => !listed.has(a.id)).map((a) => ({
        id: a.id, full_name: a.full_name, email: a.email, is_active: a.is_active,
        pending_invite: a.pending_invite, assignments: [],
      }));
      setMembers([...(data.members || []), ...adminsOnly].sort((x, y) =>
        (x.full_name || x.email || '').toLowerCase().localeCompare((y.full_name || y.email || '').toLowerCase())));
      setSites(data.sites || []);
      setOrgAdminIds(new Set((adminData.admins || []).map((a) => a.id)));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load your team');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAssign = (member) => {
    // Default to the role they already hold most of, so the dialog opens on the
    // question the admin actually came to answer.
    const counts = {};
    member.assignments.forEach((a) => { counts[a.role] = (counts[a.role] || 0) + 1; });
    const primary = Object.keys(counts).sort((a, b) => counts[b] - counts[a])[0] || 'provider';
    setRole(primary);
    setChecked(member.assignments.filter((a) => a.role === primary).map((a) => a.clinic_id));
    setEditing(member);
  };

  const onRoleChange = (nextRole) => {
    setRole(nextRole);
    setChecked(
      (editing?.assignments || []).filter((a) => a.role === nextRole).map((a) => a.clinic_id),
    );
  };

  const toggleSite = (clinicId) => {
    setChecked((prev) =>
      prev.includes(clinicId) ? prev.filter((c) => c !== clinicId) : [...prev, clinicId]);
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await orgApi.assignMember(editing.id, role, checked);
      toast.success(res.message || 'Access updated');
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not update their sites');
    } finally {
      setSaving(false);
    }
  };

  // The same shape as the enterprise and region People page — tabs by role with counts,
  // a search box — plus a site filter, because here one person spans several sites and
  // "who works at Harbour Street" is the question most often asked of this list.
  const holds = useCallback((m, key) => (key === 'org_admin'
    ? orgAdminIds.has(m.id)
    : m.assignments.some((a) => a.role === key)), [orgAdminIds]);
  const counts = useMemo(() => Object.fromEntries(TABS.map((t) => [
    t.key, t.key === 'all' ? members.length : members.filter((m) => holds(m, t.key)).length,
  ])), [members, holds]);
  const q = search.trim().toLowerCase();
  const rows = useMemo(() => members.filter((m) => {
    const key = TABS[tab].key;
    if (key !== 'all' && !holds(m, key)) return false;
    if (site && !m.assignments.some((a) => a.clinic_id === site)) return false;
    if (!q) return true;
    return (m.full_name || '').toLowerCase().includes(q)
      || (m.email || '').toLowerCase().includes(q)
      || m.assignments.some((a) => (a.clinic_name || '').toLowerCase().includes(q));
  }), [members, tab, site, q, holds]);

  return (
    <Box>
      <Typography variant="h4" fontWeight={700}>People</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Everyone working across your sites, listed once with every site they work at.
        Use <strong>Sites</strong> on a row to change where they work.
      </Typography>

      {!loading && sites.length < 2 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          With one site there is nothing to assign across yet. Add a second site and you
          can put the same provider in both without them needing a second account.
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : (
        <Card>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" sx={{ px: 2 }}>
            {TABS.map((t) => <Tab key={t.key} label={`${t.label} (${counts[t.key] ?? 0})`} />)}
          </Tabs>
          <Divider />
          <CardContent sx={{ pb: 1 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField fullWidth size="small" placeholder="Search by name, email or site…"
                value={search} onChange={(e) => setSearch(e.target.value)}
                InputProps={{ startAdornment: (
                  <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
                ) }} />
              <TextField select size="small" label="Site" value={site} sx={{ minWidth: 240 }}
                onChange={(e) => setSite(e.target.value)}
                SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }}>
                <MenuItem value="">Every site</MenuItem>
                {sites.map((st) => <MenuItem key={st.clinic_id} value={st.clinic_id}>{st.name}</MenuItem>)}
              </TextField>
            </Stack>
          </CardContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Roles</TableCell>
                  <TableCell>Works at</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      {members.length === 0
                        ? 'Nobody yet. Add providers and staff from each site\'s own screens.'
                        : 'Nobody matches that search.'}
                    </TableCell>
                  </TableRow>
                )}
                {rows.map((m) => (
                  <TableRow key={m.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{m.full_name || '—'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">{m.email}</Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                        {ROLE_CHIPS.filter((r) => holds(m, r.key)).map((r) => (
                          <Chip key={r.key} size="small" label={r.label} color={r.color}
                            variant={r.color === 'default' ? 'outlined' : 'filled'} sx={{ height: 22 }} />
                        ))}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                        {m.assignments.map((a) => (
                          <Chip key={`${a.clinic_id}:${a.role}`} size="small" variant="outlined"
                            label={`${a.clinic_name} · ${ROLE_LABELS[a.role] || a.role}`} sx={{ height: 22 }} />
                        ))}
                      </Stack>
                    </TableCell>
                    <TableCell align="center">
                      {m.pending_invite
                        ? <Chip size="small" label="Invite pending" color="warning" sx={{ height: 22 }} />
                        : <Chip size="small" label={m.is_active ? 'Active' : 'Inactive'}
                            color={m.is_active ? 'success' : 'default'} sx={{ height: 22 }} />}
                    </TableCell>
                    <TableCell align="right">
                      {/* An organisation admin who works at no site is given provider
                          sites from Administrators, not here: this dialog moves people
                          between sites, it does not recruit into one. */}
                      {m.assignments.length === 0 && orgAdminIds.has(m.id) ? (
                        <Button size="small" onClick={() => navigate('/org/admins')}>
                          Administrators
                        </Button>
                      ) : (
                        <Button size="small" startIcon={<AssignIcon />} onClick={() => openAssign(m)}>
                          Sites
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      <Dialog open={Boolean(editing)} onClose={() => setEditing(null)} fullWidth maxWidth="sm">
        <DialogTitle>
          Sites for {editing?.full_name || editing?.email}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Role" select fullWidth value={role}
              onChange={(e) => onRoleChange(e.target.value)}>
              {Object.entries(ROLE_LABELS).map(([value, label]) => (
                <MenuItem key={value} value={value}>{label}</MenuItem>
              ))}
            </TextField>
            <Typography variant="body2" color="text.secondary">
              Tick every site where they should be a {ROLE_LABELS[role].toLowerCase()}.
              Unticking removes that access.
            </Typography>
            <Box>
              {sites.map((s) => (
                <FormControlLabel
                  key={s.clinic_id}
                  control={
                    <Checkbox
                      checked={checked.includes(s.clinic_id)}
                      onChange={() => toggleSite(s.clinic_id)}
                    />
                  }
                  label={s.name}
                  sx={{ display: 'block' }}
                />
              ))}
            </Box>
            <Alert severity="info">
              Their patients and notes stay with the site they were created at — moving
              somebody between sites never moves records.
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
